import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { formatRego, formatAUMobile } from '@/lib/formatters';
import PricingDefaultsDialog from '@/components/invoices/PricingDefaultsDialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, 
  Search, 
  FileText, 
  MoreVertical,
  Eye,
  Printer,
  Mail,
  Trash2,
  CheckCircle,
  DollarSign,
  ArrowUp,
  ArrowDown,
  X,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500' }
};

export default function Invoices() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Invoice date range filter (YYYY-MM-DD inputs)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Sort order on invoice_date column — 'desc' is the operator-friendly default
  // (newest invoice first). Click the column header to flip asc/desc.
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const fetchInvoices = async () => {
    if (!user) return;
    
    try {
      let url = `${BACKEND_URL}/api/invoices?page=${page}&page_size=${pageSize}`
        + `&sort_by=invoice_date&sort_order=${sortOrder}`;
      
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (dateFrom) {
        url += `&date_from=${encodeURIComponent(dateFrom)}`;
      }
      if (dateTo) {
        url += `&date_to=${encodeURIComponent(dateTo)}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
     
  }, [user, page, statusFilter, sortOrder, dateFrom, dateTo]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user) {
        setPage(1);
        fetchInvoices();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleDelete = async (invoiceId, docNo) => {
    if (!window.confirm(`Delete invoice ${docNo}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Invoice deleted');
        await fetchInvoices();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || `Failed to delete invoice (HTTP ${response.status})`);
      }
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
        fetchInvoices();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // iter138.7 — One-click Paid toggle for the Invoices list. Sets
  // amount_paid to the invoice total and flips status to 'paid'. Untick
  // resets amount_paid to 0 and reverts status to 'sent' (or 'draft' if
  // it was never sent).
  const handleTogglePaid = async (invoice, nextPaid) => {
    try {
      const total = Number(invoice.total) || 0;
      const payload = nextPaid
        ? { amount_paid: total, status: 'paid' }
        : { amount_paid: 0, status: invoice.status === 'paid' ? 'sent' : invoice.status };
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoice.invoice_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(nextPaid
          ? `${invoice.doc_no || 'Invoice'} marked PAID ($${total.toFixed(2)})`
          : `${invoice.doc_no || 'Invoice'} payment cleared`);
        fetchInvoices();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update payment');
      }
    } catch (_e) {
      toast.error('Failed to update payment');
    }
  };

  // True when invoice has an unpaid balance AND due_date is in the past.
  const isOverdue = (invoice) => {
    if (!invoice.due_date) return false;
    if ((invoice.amount_due || 0) <= 0) return false;
    if (invoice.status === 'paid' || invoice.status === 'cancelled') return false;
    const due = new Date(invoice.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="invoices-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="font-['Chivo'] font-bold text-3xl sm:text-4xl text-slate-900 mb-1">
              Invoices
            </h1>
            <p className="text-slate-500">
              {total} invoice{total !== 1 ? 's' : ''} total
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <PricingDefaultsDialog user={user} />
            <Button 
              onClick={() => navigate('/invoices/new')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-invoice-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6 items-start sm:items-end">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by invoice #, customer, phone, rego…"
              className="pl-10"
              data-testid="invoices-search"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="invoices-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Invoice-date range filter */}
          <div className="flex flex-col">
            <label className="text-[11px] font-medium text-slate-500 mb-1 ml-1">Invoice date from</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-40"
              data-testid="invoices-date-from"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] font-medium text-slate-500 mb-1 ml-1">to</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-40"
              data-testid="invoices-date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-slate-500 hover:text-slate-900 mb-px"
              data-testid="invoices-date-clear"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Clear dates
            </Button>
          )}
        </div>

        {/* Invoices Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10" title="Tick to mark this invoice paid in full">Paid</TableHead>
                <TableHead>Invoice No</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => { setSortOrder(o => o === 'desc' ? 'asc' : 'desc'); setPage(1); }}
                    className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors"
                    data-testid="invoices-sort-date"
                    title={`Sort by invoice date (${sortOrder === 'desc' ? 'newest first' : 'oldest first'})`}
                  >
                    Date
                    {sortOrder === 'desc'
                      ? <ArrowDown className="h-3.5 w-3.5 text-blue-600" />
                      : <ArrowUp className="h-3.5 w-3.5 text-blue-600" />}
                  </button>
                </TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 mb-4">No invoices found</p>
                    <Button onClick={() => navigate('/invoices/new')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Invoice
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map(invoice => {
                  const overdue = isOverdue(invoice);
                  const fullyPaid = invoice.status === 'paid' || (invoice.amount_due || 0) <= 0;
                  return (
                  <TableRow 
                    key={invoice.invoice_id}
                    className={`cursor-pointer hover:bg-slate-50 ${overdue ? 'bg-rose-50/40' : ''}`}
                    onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                  >
                    {/* iter138.7 — Quick-pay checkbox. Click stops row
                        nav so SM can flag paid without opening the
                        invoice. Renders empty for cancelled invoices. */}
                    <TableCell onClick={(e) => e.stopPropagation()} className="text-center">
                      {invoice.status !== 'cancelled' && (
                        <Checkbox
                          checked={fullyPaid}
                          onCheckedChange={(checked) => handleTogglePaid(invoice, !!checked)}
                          data-testid={`invoice-paid-checkbox-${invoice.invoice_id}`}
                          title={fullyPaid
                            ? 'Click to clear payment'
                            : `Mark as paid ($${(invoice.total || 0).toFixed(2)})`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-blue-600">
                      {invoice.doc_no}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{invoice.customer_name || '-'}</div>
                      <div className="text-xs text-slate-500">{formatAUMobile(invoice.customer_phone) || ''}</div>
                    </TableCell>
                    <TableCell>
                      {formatRego(invoice.vehicle_rego) || '-'}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {formatDate(invoice.invoice_date)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(invoice.total)}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${(invoice.amount_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(invoice.amount_due || invoice.total)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className={STATUS_CONFIG[invoice.status]?.color}>
                          {STATUS_CONFIG[invoice.status]?.label || invoice.status}
                        </Badge>
                        {/* iter138.9ae — surface ON-ACCOUNT explicitly so
                            SM can spot credit invoices vs walk-in/cash at
                            a glance. Stays alongside the status (not
                            replacing it) — an On-Account invoice can
                            still be Draft / Sent / Paid. */}
                        {(invoice.is_on_account || invoice.account_id || invoice.payment_method === 'account') && (
                          <Badge
                            className="bg-indigo-100 text-indigo-800 border border-indigo-300 gap-0.5"
                            data-testid={`invoice-on-account-badge-${invoice.invoice_id}`}
                            title="Billed to a credit account — will appear on the customer's monthly statement"
                          >
                            ★ ON ACCOUNT
                          </Badge>
                        )}
                        {overdue && (
                          <Badge
                            className="bg-rose-100 text-rose-800 border border-rose-300 gap-0.5"
                            data-testid={`invoice-overdue-badge-${invoice.invoice_id}`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Overdue
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View / Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled>
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled>
                            <Mail className="h-4 w-4 mr-2" />
                            Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {invoice.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.invoice_id, 'paid')}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Paid
                            </DropdownMenuItem>
                          )}
                          {invoice.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.invoice_id, 'sent')}>
                              <Mail className="h-4 w-4 mr-2" />
                              Mark as Sent
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {user && user.role === 'super_admin' && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(invoice.invoice_id, invoice.doc_no)}
                              className="text-red-600"
                              data-testid={`delete-invoice-${invoice.invoice_id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
